import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Building,
  FileText,
  Heart,
  Home,
  Zap,
  Users,
  AlertCircle,
  Shield,
  CheckCircle,
  Clock,
  Edit
} from "lucide-react";
import { ReferralAcceptance } from "@/components/ReferralAcceptance";
import { useFormattedDate } from '../hooks/useUserTimezone';

interface Referral {
  id: string | number;
  referralNumber?: string; // Unique 10-digit referral number
  // Member Information
  memberMedicaidId: string;
  memberName: string;
  dateOfBirth: string;
  zipCode: string;
  preferredLanguage: string;
  phoneNumber?: string;
  email?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;

  // Health Plan Information
  healthPlan: string;
  planType: "Managed Care" | "Fee-for-Service";

  // Provider Information
  referringProvider: {
    name: string;
    npi: string;
    organization: string;
    phone: string;
  };

  // Clinical Context
  primaryDiagnosis: string;
  riskScore?: number;
  sdohNeeds: string[];
  housingSubcategories?: string[];
  transportationSubcategories?: string[];
  urgencyLevel: "Immediate" | "Within 24h" | "Within 1 Week" | "Routine";
  availability?: string;
  additionalNotes?: string;

  // Authorization & Compliance
  authorizationRequired: boolean;
  consentObtained: boolean;
  hipaaCompliant: boolean;

  // Status & Timestamps
  status: "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "Authorized" | "In Progress" | "On Hold" | "Completed";
  createdAt: string;
  timeline: string;

  // Acceptance Workflow (PHI Integration)
  matchedProviderId?: string;
  acceptanceStatus?: 'pending' | 'accepted' | 'rejected' | 'expired';
  acceptedAt?: string;
  rejectedAt?: string;
  expiresAt?: string;
  phiEmailSent?: boolean;
  emailSentTo?: string;
}

interface ReferralDetailViewProps {
  referral: Referral;
  onBack: () => void;
  onEdit?: () => void;
}

export function ReferralDetailView({ referral, onBack, onEdit }: ReferralDetailViewProps) {
  const { fmt } = useFormattedDate();
  const formatDate = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "Immediate": return "red";
      case "Within 24h": return "orange";
      case "Within 1 Week": return "yellow";
      default: return "green";
    }
  };

  const urgencyColor = getUrgencyColor(referral.urgencyLevel);

  return (
    <div className="w-full relative bg-paper">
      {/* Content */}
      <div className="relative z-10 p-4 md:p-6 lg:p-10 pt-4">
        <div className="w-full max-w-7xl mx-auto">
          {/* Header */}
          <div className="w-full bg-surface pt-6 pb-8 px-8 border border-v2border shadow-sm rounded-2xl mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onBack}
                  className="flex items-center space-x-2 border border-v2border bg-surface text-ink hover:bg-paper-2 rounded-xl"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </Button>
                <div>
                  <p className="text-[13px] text-ink-500">
                    Referral #{referral.referralNumber || String(referral.id).slice(0, 10)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Badge
                  variant="secondary"
                  className={`px-4 py-2 text-sm font-semibold rounded-lg ${
                    referral.status === "Pending"
                      ? "bg-orange-100 text-orange-800 border border-orange-300"
                      : referral.status === "Matched" || referral.status === "Authorized"
                      ? "bg-blue-100 text-blue-800 border border-blue-300"
                      : referral.status === "In Progress"
                      ? "bg-purple-100 text-purple-800 border border-purple-300"
                      : referral.status === "Completed"
                      ? "bg-green-100 text-green-800 border border-green-300"
                      : "bg-paper-2 text-ink border border-v2border"
                  }`}
                >
                  {referral.status}
                </Badge>
                {onEdit && (
                  <Button
                    onClick={onEdit}
                    className="flex items-center space-x-2 bg-v2accent hover:bg-v2accent/90 text-white rounded-xl"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit Referral</span>
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Member & Provider Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Member Information */}
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                <CardHeader className="border-b border-v2border">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-3 bg-paper-2 rounded-2xl">
                      <User className="w-6 h-6 text-ink-500" />
                    </div>
                    <span>Member Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-slate-600">Full Name</label>
                      <p className="text-lg font-bold text-gray-900">{referral.memberName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-600">Medicaid ID</label>
                      <p className="text-lg font-bold text-gray-900">{referral.memberMedicaidId}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-slate-600 flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>Date of Birth</span>
                      </label>
                      <p className="text-base font-medium text-gray-800">{referral.dateOfBirth}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-600">Preferred Language</label>
                      <p className="text-base font-medium text-gray-800">{referral.preferredLanguage}</p>
                    </div>
                  </div>

                  {referral.phoneNumber && (
                    <div>
                      <label className="text-sm font-semibold text-slate-600 flex items-center space-x-1">
                        <Phone className="w-4 h-4" />
                        <span>Phone Number</span>
                      </label>
                      <p className="text-base font-medium text-gray-800">{referral.phoneNumber}</p>
                    </div>
                  )}

                  {referral.email && (
                    <div>
                      <label className="text-sm font-semibold text-slate-600 flex items-center space-x-1">
                        <Mail className="w-4 h-4" />
                        <span>Email</span>
                      </label>
                      <p className="text-base font-medium text-gray-800">{referral.email}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-slate-600 flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>Address</span>
                    </label>
                    <div className="text-base font-medium text-gray-800">
                      {referral.streetAddress || referral.city || referral.state || referral.country ? (
                        <>
                          {referral.streetAddress && <p>{referral.streetAddress}</p>}
                          <p>{referral.city && referral.state
                            ? `${referral.city}, ${referral.state} ${referral.zipCode}`
                            : referral.zipCode}
                          </p>
                          {referral.country && <p>{referral.country}</p>}
                        </>
                      ) : (
                        <span className="text-gray-500 italic">Address not provided</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Health Plan Information */}
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                <CardHeader className="border-b border-v2border">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-3 bg-paper-2 rounded-2xl">
                      <Building className="w-6 h-6 text-ink-500" />
                    </div>
                    <span>Health Plan Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-600">Health Plan</label>
                    <p className="text-lg font-bold text-gray-900">{referral.healthPlan || <span className="text-gray-500 italic">Not provided</span>}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-600">Plan Type</label>
                    <p className="text-base font-medium text-gray-800">{referral.planType || <span className="text-gray-500 italic">Not provided</span>}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Provider Information */}
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                <CardHeader className="border-b border-v2border">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-3 bg-paper-2 rounded-2xl">
                      <FileText className="w-6 h-6 text-ink-500" />
                    </div>
                    <span>Referring Provider</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-600">Provider Name</label>
                    <p className="text-lg font-bold text-gray-900">{referral.referringProvider.name || <span className="text-gray-500 italic">Not provided</span>}</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-slate-600">NPI Number</label>
                      <p className="text-base font-medium text-gray-800">{referral.referringProvider.npi || <span className="text-gray-500 italic">Not provided</span>}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-600 flex items-center space-x-1">
                        <Phone className="w-4 h-4" />
                        <span>Phone</span>
                      </label>
                      <p className="text-base font-medium text-gray-800">{referral.referringProvider.phone || <span className="text-gray-500 italic">Not provided</span>}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-600">Organization</label>
                    <p className="text-base font-medium text-gray-800">{referral.referringProvider.organization || <span className="text-gray-500 italic">Not provided</span>}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Service Needs */}
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                <CardHeader className="border-b border-v2border">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-3 bg-paper-2 rounded-2xl">
                      <Heart className="w-6 h-6 text-ink-500" />
                    </div>
                    <span>Services Requested</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Housing Services */}
                  {referral.housingSubcategories && referral.housingSubcategories.length > 0 && (
                    <div className="bg-paper-2 p-4 rounded-lg border border-v2border">
                      <div className="flex items-center space-x-2 mb-3">
                        <Home className="w-5 h-5 text-ink-500" />
                        <span className="text-ink font-bold text-base">Housing Assistance</span>
                      </div>
                      <ul className="ml-7 space-y-2">
                        {referral.housingSubcategories.map((sub, idx) => (
                          <li key={idx} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-ink-500" />
                            <span className="text-ink">{sub}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Transportation Services */}
                  {referral.transportationSubcategories && referral.transportationSubcategories.length > 0 && (
                    <div className="bg-paper-2 p-4 rounded-lg border border-v2border">
                      <div className="flex items-center space-x-2 mb-3">
                        <Zap className="w-5 h-5 text-ink-500" />
                        <span className="text-ink font-bold text-base">Transportation Assistance</span>
                      </div>
                      <ul className="ml-7 space-y-2">
                        {referral.transportationSubcategories.map((sub, idx) => (
                          <li key={idx} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-ink-500" />
                            <span className="text-ink">{sub}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Other SDOH Needs */}
                  {referral.sdohNeeds && referral.sdohNeeds.length > 0 && (
                    <div className="bg-paper-2 p-4 rounded-lg border border-v2border">
                      <div className="flex items-center space-x-2 mb-3">
                        <Users className="w-5 h-5 text-ink-500" />
                        <span className="text-ink font-bold text-base">Other Social Services</span>
                      </div>
                      <ul className="ml-7 space-y-2">
                        {referral.sdohNeeds.map((need, idx) => (
                          <li key={idx} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-ink-500" />
                            <span className="text-ink">{need}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Clinical & Administrative */}
            <div className="space-y-6">
              {/* Urgency & Timeline */}
              <Card className={`border shadow-sm bg-white rounded-xl overflow-hidden border-${urgencyColor}-200/50`}>
                <CardHeader className={`bg-gradient-to-r from-${urgencyColor}-50/90 to-${urgencyColor}-100/90 border-b border-${urgencyColor}-200/40`}>
                  <CardTitle className="flex items-center space-x-3">
                    <div className={`p-3 bg-${urgencyColor}-600 rounded-2xl shadow-sm`}>
                      <AlertCircle className="w-6 h-6 text-white" />
                    </div>
                    <span>Urgency</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="text-center">
                    <p className={`text-2xl font-bold text-${urgencyColor}-800`}>{referral.urgencyLevel}</p>
                    {referral.availability && (
                      <div className="mt-4">
                        <label className="text-sm font-semibold text-slate-600 flex items-center justify-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>Member Availability</span>
                        </label>
                        <p className="text-sm text-gray-800 mt-1">{referral.availability}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Clinical Information */}
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                <CardHeader className="border-b border-v2border">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-3 bg-paper-2 rounded-2xl">
                      <Heart className="w-6 h-6 text-ink-500" />
                    </div>
                    <span>Clinical Context</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-600">Primary Diagnosis</label>
                    <p className="text-base font-medium text-gray-800">{referral.primaryDiagnosis}</p>
                  </div>
                  {referral.riskScore !== undefined && (
                    <div>
                      <label className="text-sm font-semibold text-slate-600">Risk Score</label>
                      <p className="text-base font-medium text-gray-800">{referral.riskScore}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Authorization & Compliance */}
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                <CardHeader className="border-b border-v2border">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-3 bg-paper-2 rounded-2xl">
                      <Shield className="w-6 h-6 text-ink-500" />
                    </div>
                    <span>Compliance</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Prior Authorization</span>
                    {referral.authorizationRequired ? (
                      <Badge className="bg-orange-100 text-orange-800 border border-orange-300">Required</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-800 border border-gray-300">Not Required</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Member Consent</span>
                    {referral.consentObtained ? (
                      <Badge className="bg-green-100 text-green-800 border border-green-300">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Obtained
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 border border-red-300">Not Obtained</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">HIPAA Compliant</span>
                    {referral.hipaaCompliant ? (
                      <Badge className="bg-green-100 text-green-800 border border-green-300">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Yes
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 border border-red-300">No</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                <CardHeader className="border-b border-v2border">
                  <CardTitle className="flex items-center space-x-3">
                    <div className="p-3 bg-paper-2 rounded-2xl">
                      <Calendar className="w-6 h-6 text-ink-500" />
                    </div>
                    <span>Timeline</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div>
                    <label className="text-sm font-semibold text-slate-600">Created</label>
                    <p className="text-base font-medium text-gray-800">{formatDate(referral.createdAt)}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Notes */}
              {referral.additionalNotes && (
                <Card className="border border-v2border shadow-sm bg-surface rounded-xl overflow-hidden">
                  <CardHeader className="border-b border-v2border">
                    <CardTitle className="flex items-center space-x-3">
                      <div className="p-3 bg-paper-2 rounded-2xl">
                        <FileText className="w-6 h-6 text-ink-500" />
                      </div>
                      <span>Additional Notes</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-base text-gray-800 italic">{referral.additionalNotes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Referral Acceptance Section - PHI WORKFLOW */}
              {(referral.status === 'Matched' && referral.matchedProviderId) && (
                <Card className="border border-v2border shadow-lg bg-surface rounded-xl overflow-hidden">
                  <CardHeader className="bg-v2accent-soft border-b border-v2border">
                    <CardTitle className="flex items-center space-x-3 text-v2accent-ink">
                      <div className="p-3 bg-v2accent rounded-2xl">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <span>Provider Response Required</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <ReferralAcceptance
                      referralId={String(referral.id)}
                      providerId={referral.matchedProviderId}
                      onAccept={() => {
                        // Trigger parent component to refresh referral data
                        console.log('Referral accepted - refresh data');
                        window.location.reload();
                      }}
                      onReject={() => {
                        // Trigger parent component to refresh referral data
                        console.log('Referral rejected - refresh data');
                        window.location.reload();
                      }}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReferralDetailView;
